import { Divider, Text } from "@flat-ui";
import { Grid } from "@mui-wrapper";
import { QueryParams } from "@router/route-params";
import { useSearchParams } from "react-router-dom";
import { filterOAuthProviders } from "@utils/oauth-provider-utils";
import { runtimeConfig } from "@src/runtime-config";
import {
  PreassignedServerWithoutGenericOAuth,
  RegisterableServer,
} from "@custom-types/server-types";
import { useCallback } from "react";
import { faroCoreApiClient } from "@api/core-api/core-api-utils";
import { isValidUlmClient } from "@utils/ulm-utils";
import { UlmClientType } from "@custom-types/ulm-types";
import { CLIENT_CONFIGURATION } from "@constants/ulm-constants";
import { useErrorHandlerContext } from "@context-providers/error-handler/error-handler-context";
import { FaroButtonSSO } from "@components/faro-sso-button";
import { AuthMethod } from "@custom-types/signup-types";
import { isPreassignedServerWithoutGenericOAuth } from "@utils/servers-utils";
import { PROVIDER_INFO } from "@constants/provider-constants";
import { UlmButton } from "@components/ulm/ulm-button";

/** Maps login provider identifiers to authentication methods */
const AUTH_METHOD_MAP: Record<
  PreassignedServerWithoutGenericOAuth["loginProvider"]["identifier"],
  AuthMethod
> = {
  email: "email",
  GOOGLE: "google",
  APPLE: "apple",
  PROCORE: "procore",
  AUTODESK: "autodesk",
  FACEBOOK: "facebook",
  ["FARO-SPHERE"]: "faro-sphere",
};

interface Props {
  /** Email address of the user */
  email: string;

  /** Registerable server entity for the email address */
  server: RegisterableServer;

  /** Whether the email address is being validated for the email/password authentication method */
  isLoading: boolean;

  /** Callback to set the loading state */
  setIsLoading: (isLoading: boolean) => void;

  /** Callback to set the selected authentication method */
  setAuthMethod: (authMethod: AuthMethod) => void;
}

/**
 * Renders the available authentication method options during signup flows.
 * It takes into the available authentication methods for the current region and
 * the available authentication methods for the passed email address.
 */
export function SignupAuthMethod({
  email,
  server,
  isLoading,
  setAuthMethod,
  setIsLoading,
}: Props): JSX.Element {
  const { handleError } = useErrorHandlerContext();
  const [searchParams] = useSearchParams();
  const providersParam = searchParams.get(QueryParams.providers);
  const { providers, isEmailAllowed } = filterOAuthProviders(
    runtimeConfig.oauth.providers,
    providersParam
  );

  const clientParam = searchParams.get(QueryParams.client);
  const client: UlmClientType =
    clientParam && isValidUlmClient(clientParam) ? clientParam : "default";

  const trialTypeParam = searchParams.get(QueryParams.trialType);
  const trialType = trialTypeParam || CLIENT_CONFIGURATION[client].trialType;

  /**
   * Validates that the provided email address can be used to create a
   * trial account with email/password authentication method.
   */
  const onEmailPasswordSelected = useCallback(async (): Promise<void> => {
    setIsLoading(true);

    try {
      await faroCoreApiClient.users.validateEmail({
        email,
        trialType,
      });

      setAuthMethod("email");
    } catch (error) {
      handleError({
        id: `validateEmail-${Date.now().toString()}`,
        title: "Failed to validate the email address for email sign up.",
        error,
        uiType: "toast",
      });
    }

    setIsLoading(false);
  }, [email, handleError, setAuthMethod, setIsLoading, trialType]);

  /** Handles the selection of an authentication method */
  function handleSelection(authMethod: AuthMethod): void {
    if (authMethod === "email") {
      void onEmailPasswordSelected();
    } else {
      setAuthMethod(authMethod);
    }
  }

  // If the email address of the user has a preassigned server entity then only render
  // the button for the preassigned authentication method.
  if (isPreassignedServerWithoutGenericOAuth(server)) {
    const authMethod = AUTH_METHOD_MAP[server.loginProvider.identifier];
    return authMethod === "email" ? (
      <UlmButton
        onClick={() => handleSelection(authMethod)}
        isLoading={isLoading}
        isLoadingText="Validating"
      >
        Sign up with a password
      </UlmButton>
    ) : (
      <FaroButtonSSO
        label={PROVIDER_INFO[authMethod]?.providerName}
        icon={PROVIDER_INFO[authMethod]?.icon}
        onClick={() => handleSelection(authMethod)}
        isLoading={isLoading}
      />
    );
  }

  return (
    <>
      {isEmailAllowed && (
        <UlmButton
          onClick={() => handleSelection("email")}
          isLoading={isLoading}
          isLoadingText="Validating"
        >
          Sign up with a password
        </UlmButton>
      )}
      {isEmailAllowed && providers.length > 0 && (
        <Divider sx={{ width: "100%", mt: "32px" }}>
          <Text variant="bodyL" component="p" sx={{ color: "text.secondary" }}>
            or sign up with
          </Text>
        </Divider>
      )}
      <Grid
        container
        spacing={2}
        sx={{
          width: "100%",
          marginY: "16px",
        }}
      >
        {providers.map((provider) => (
          <Grid key={provider} size={{ xs: 12, sm: 6 }}>
            <FaroButtonSSO
              label={PROVIDER_INFO[provider]?.providerName}
              icon={PROVIDER_INFO[provider]?.icon}
              onClick={() => handleSelection(provider)}
              disabled={isLoading}
            />
          </Grid>
        ))}
      </Grid>
    </>
  );
}
